import * as ics from "ics";
import { NodeEdge } from "@hex-insights/app-modules";
import {
	addTimeToDate,
	compareBooleansDesc,
	compareMultiLevel,
	compareObjects,
	compareStringsAsc,
	compareStringsDesc,
	NullablePick,
	stringToLocalDate,
	toLocalDateString,
} from "@hex-insights/core";
import { CalendarEvent, SharingRecord, UserGroup } from "../../../Utilities";

export type CalendarEventAttendee<U, UG> =
	| {
			user: U;
			userGroup?: undefined;
	  }
	| {
			user?: undefined;
			userGroup: UG;
	  };

export function startTime(event: Pick<CalendarEvent, "isAllDay" | "startTime" | "startDate">) {
	if (event.isAllDay) {
		return event.startDate!;
	}
	return event.startTime!;
}

export function startTimeDate(event: Pick<CalendarEvent, "isAllDay" | "startTime" | "startDate">) {
	if (event.isAllDay) {
		return stringToLocalDate(event.startDate!, "day");
	}
	return new Date(event.startTime!);
}

export function endTime(event: Pick<CalendarEvent, "isAllDay" | "endTime" | "endDate">) {
	if (event.isAllDay) {
		return event.endDate!;
	}
	return event.endTime!;
}

export function endTimeDate(event: Pick<CalendarEvent, "isAllDay" | "endTime" | "endDate">) {
	if (event.isAllDay) {
		return stringToLocalDate(event.endDate!, "day");
	}
	return new Date(event.endTime!);
}

export function groupName(event: {
	sharingRecord: (Pick<SharingRecord, "isPublic"> & { userGroups?: Pick<UserGroup, "name">[] }) | null;
	author?: NullablePick<CalendarEvent["author"], "isCurrent">;
}) {
	if (event.sharingRecord && event.sharingRecord.userGroups && event.sharingRecord.userGroups.length > 0) {
		return event.sharingRecord.userGroups[0].name;
	}
	if (event.sharingRecord?.isPublic) {
		return "Everyone";
	}
	if (event.author?.isCurrent) {
		return "Personal";
	}
	return "Invited";
}

const publicEventColor = "var(--brand-colors--primary)";
const privateEventColor = "#ad001a";

export function color(event: {
	sharingRecord: (Pick<SharingRecord, "isPublic"> & { userGroups?: Pick<UserGroup, "color">[] }) | null;
}) {
	if (event.sharingRecord && event.sharingRecord.userGroups && event.sharingRecord.userGroups.length > 0) {
		return event.sharingRecord.userGroups[0].color;
	}
	return event.sharingRecord?.isPublic ? publicEventColor : privateEventColor;
}

/**
 * Returns a comparison function for sorting calendar events by start time (00:00:00 for all-day events).
 */
export function compareCalendarEventsAsc() {
	return compareObjects<Pick<CalendarEvent, "isAllDay" | "startTime" | "startDate">>(
		(e) => startTimeDate(e).toISOString(),
		compareStringsAsc(),
	);
}

/**
 * Returns a comparison function for sorting calendar events by start time (00:00:00 for all-day events).
 */
export function compareCalendarEventsDesc() {
	return compareObjects<Pick<CalendarEvent, "isAllDay" | "startTime" | "startDate">>(
		(e) => startTimeDate(e).toISOString(),
		compareStringsDesc(),
	);
}

/**
 * Returns a comparison function for sorting calendar events by start time (00:00:00 for all-day events).
 */
export function compareCalendarEventEdgesAsc() {
	return compareObjects<NodeEdge<Pick<CalendarEvent, "isAllDay" | "startTime" | "startDate">>>(
		(e) => startTimeDate(e.node).toISOString(),
		compareStringsAsc(),
	);
}

/**
 * Returns a comparison function for sorting calendar events by start time (00:00:00 for all-day events).
 */
export function compareCalendarEventEdgesDesc() {
	return compareObjects<NodeEdge<Pick<CalendarEvent, "isAllDay" | "startTime" | "startDate">>>(
		(e) => startTimeDate(e.node).toISOString(),
		compareStringsDesc(),
	);
}

/**
 * Returns a comparison function for sorting calendar events that puts all day events first followed by timed events
 * ordered by their start time.
 *
 * @note Intended for sorting on a single day.
 */
export function compareCalendarEventsOnDate() {
	const compare = compareMultiLevel([compareBooleansDesc(), compareStringsAsc()]);
	const transform = (e: Pick<CalendarEvent, "isAllDay" | "startTime">) => [e.isAllDay, e.startTime];
	return (a: Pick<CalendarEvent, "isAllDay" | "startTime">, b: Pick<CalendarEvent, "isAllDay" | "startTime">) => {
		return compare(transform(a), transform(b));
	};
}

/**
 * Returns a comparison function for sorting calendar event edges that puts all day events first followed by timed
 * events ordered by their start time.
 *
 * @note Intended for sorting on a single day.
 */
export function compareCalendarEventEdgesOnDate() {
	const compare = compareMultiLevel([compareBooleansDesc(), compareStringsAsc()]);
	const transform = (e: NodeEdge<Pick<CalendarEvent, "isAllDay" | "startTime">>) => [e.node.isAllDay, e.node.startTime];
	return (
		a: NodeEdge<Pick<CalendarEvent, "isAllDay" | "startTime">>,
		b: NodeEdge<Pick<CalendarEvent, "isAllDay" | "startTime">>,
	) => {
		return compare(transform(a), transform(b));
	};
}

export async function toICal(
	calendarEvent: Pick<
		CalendarEvent,
		"name" | "description" | "isAllDay" | "startTime" | "endTime" | "startDate" | "endDate" | "location"
	>,
) {
	return new Promise<File>((resolve, reject) => {
		ics.createEvent(
			{
				title: calendarEvent.name,
				description: calendarEvent.description,
				start: toICalTime(calendarEvent.isAllDay, calendarEvent.startTime, calendarEvent.startDate),
				end: toICalTime(calendarEvent.isAllDay, calendarEvent.endTime, calendarEvent.endDate, true),
				startInputType: "utc",
				startOutputType: "utc",
				location: calendarEvent.location,
			},
			(error, value) => {
				if (error) {
					reject(error);
					return;
				}
				resolve(new File([value], `${calendarEvent.name.replace(/\s+/g, "-")}.ics`, { type: "text/calendar" }));
			},
		);
	});
}

function toICalTime(isAllDay: boolean, time: string | null, date: string | null, isEnd?: boolean) {
	if (isAllDay) {
		if (!date) {
			return [0, 0, 0] as [number, number, number];
		}
		if (isEnd) {
			date = toLocalDateString(addTimeToDate(stringToLocalDate(date, "day"), [1, "day"]));
		}
		return date
			.split("T")[0]
			.split("-")
			.map((e) => Number(e)) as [number, number, number];
	}
	if (!time) {
		return [0, 0, 0, 0, 0] as [number, number, number, number, number];
	}
	time = new Date(time).toISOString();
	const match = time.match(/(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
	if (!match) {
		return [0, 0, 0, 0, 0] as [number, number, number, number, number];
	}
	return match.slice(1).map((e) => Number(e)) as [number, number, number, number, number];
}
